import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { hubModuleAllowsAdminSettings } from "@/lib/subapp-access";
import { useIntegratorMe } from "@/lib/me-query";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { FilePicker } from "@/components/ui/FilePicker";
import { PageTitleWithHelp, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import { RepFciVolumeHoverHint } from "@/components/commissions/RepFciVolumeHoverHint";
import { getCommissionRules, listCommissionRates, commissionsRatesQueryKey, commissionsRulesQueryKey, commissionsStableListQueryOptions } from "./commissionsApi";

function formatRate(raw: unknown): string {
  if (raw == null || raw === "") {
    return "";
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return String(raw);
  }
  return `${(value * 100).toFixed(2).replace(/\.00$/, "")}%`;
}

function formatQuantity(raw: unknown): string {
  if (raw == null || raw === "") {
    return "—";
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return String(raw);
  }
  return Math.round(value).toLocaleString("en-US");
}

function parseRate(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  if (trimmed.endsWith("%")) {
    const parsed = Number.parseFloat(trimmed.slice(0, -1));
    return Number.isFinite(parsed) ? parsed / 100 : null;
  }
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed > 1 ? parsed / 100 : parsed;
}

function formatTerminatedDate(raw: unknown): string {
  if (raw == null || raw === "") {
    return "—";
  }
  const text = String(raw).slice(0, 10);
  const parsed = new Date(`${text}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return text;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function isRepAgencyTerminated(row: { commission_terminated_at?: unknown }): boolean {
  return row.commission_terminated_at != null && String(row.commission_terminated_at).trim() !== "";
}

/** Row 1 header height — row 2 sticky `top` must match (`top-10` = 2.5rem). */
const RATES_HEADER_ROW_1 = "sticky top-0 z-20 h-10 border-b border-slate-200 bg-slate-50 px-2";
const RATES_HEADER_ROW_2 =
  "sticky top-10 z-20 min-h-10 border-b border-slate-200 bg-slate-50 px-2 align-middle font-medium whitespace-normal leading-tight";
const RATES_BODY_CELL = "px-2 py-1.5 align-middle";
const RATES_NUMERIC_CELL = `${RATES_BODY_CELL} text-right tabular-nums`;
const RATE_INPUT_CLASS = "h-7 w-[4.5rem] px-1.5 py-0 text-xs text-right tabular-nums";

function FciQuantityCell({
  repAgencyId,
  calendarYear,
  quantity,
}: {
  repAgencyId: number;
  calendarYear: number;
  quantity: unknown;
}) {
  const display = formatQuantity(quantity);
  if (display === "—") {
    return <span className="block text-right tabular-nums text-slate-700">{display}</span>;
  }
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <span className="block cursor-help text-right tabular-nums text-slate-700 underline decoration-dotted underline-offset-2">
          {display}
        </span>
      }
    >
      <RepFciVolumeHoverHint repAgencyId={repAgencyId} calendarYear={calendarYear} />
    </HoverHintTrigger>
  );
}

function TerminateRepAgencyModal({
  repAgencyName,
  initialDate,
  onCancel,
  onConfirm,
  isPending,
}: {
  repAgencyName: string;
  initialDate: string;
  onCancel: () => void;
  onConfirm: (terminatedAt: string) => void;
  isPending: boolean;
}) {
  const [terminatedAt, setTerminatedAt] = useState(initialDate);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Terminate rep agency</h2>
          <p className="mt-1 text-sm text-slate-600">{repAgencyName}</p>
        </div>
        <div className="space-y-4 px-6 py-4">
          <p className="text-sm text-slate-700">
            Sets the termination date on this rep agency. Monthly commission runs use this date to stop paying
            the rep after the chosen day.
          </p>
          <div>
            <Label htmlFor="rep-agency-terminated-at">Terminated on</Label>
            <Input
              id="rep-agency-terminated-at"
              type="date"
              className="mt-1"
              value={terminatedAt}
              onChange={(event) => setTerminatedAt(event.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(terminatedAt)}
            disabled={isPending || terminatedAt.trim() === ""}
          >
            {isPending ? "Saving…" : "Confirm termination"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CommissionRatesPage() {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const canEdit = hubModuleAllowsAdminSettings(meQuery.data, "commissions");
  const readOnlyTitle = "Commissions module administrator access is required to edit rep agency rates.";
  const [drafts, setDrafts] = useState<Record<number, Record<string, string>>>({});
  const [terminateTarget, setTerminateTarget] = useState<{ id: number; name: string } | null>(null);
  const [showTerminated, setShowTerminated] = useState(false);
  const currentCalendarYear = new Date().getFullYear();
  const previousCalendarYear = currentCalendarYear - 1;

  const listQuery = useQuery({
    queryKey: commissionsRatesQueryKey,
    queryFn: listCommissionRates,
    ...commissionsStableListQueryOptions,
  });
  const rulesQuery = useQuery({
    queryKey: commissionsRulesQueryKey,
    queryFn: getCommissionRules,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });
  const tierBonusRule = rulesQuery.data?.rules.find((rule) => rule.id === "tier_bonus");
  const newEndUserRule = rulesQuery.data?.rules.find((rule) => rule.id === "new_end_user");
  const pageHoverHint =
    newEndUserRule != null
      ? `Edit rep agency commission rates used by monthly runs and GOSI accrual journal entries. New end user bonus: ${newEndUserRule.summary}`
      : "Edit rep agency commission rates used by monthly runs and GOSI accrual journal entries.";
  const fciQuantityHint =
    tierBonusRule?.summary ??
    "Product-category sales-order unit volume for the rep in the calendar year (prior-year volume drives tier bonus on the next year's runs).";
  const bonusRateHint =
    "Tier bonus rate = selected tier rate − base rate, derived from FCI quantity and the tier columns.";

  const allRows = listQuery.data?.rows ?? [];
  const visibleRows = showTerminated ? allRows : allRows.filter((row) => !isRepAgencyTerminated(row));
  const terminatedHiddenCount = allRows.length - visibleRows.length;

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/inside-sales/rep-agencies/import-comm-rates", {
        method: "POST",
        headers: token != null ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Import failed (${response.status}).`);
      }
      return body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commissionsRatesQueryKey, exact: true });
      void queryClient.invalidateQueries({ queryKey: ["inside-sales", "rep-agencies"] });
    },
  });

  const saveMutation = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      apiFetch(`/api/commissions/rates/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: (_data, variables) => {
      setDrafts((current) => {
        if (current[variables.id] == null) {
          return current;
        }
        const next = { ...current };
        delete next[variables.id];
        return next;
      });
      setTerminateTarget(null);
      void queryClient.invalidateQueries({ queryKey: commissionsRatesQueryKey, exact: true });
    },
  });

  function saveRow(id: number, draft: Record<string, string>): void {
    const body: Record<string, unknown> = {};
    for (const key of [
      "commission_rate",
      "commission_rate_tier_0_1000",
      "commission_rate_tier_1001_5000",
      "commission_rate_tier_5001_plus",
    ] as const) {
      if (draft[key] != null) {
        body[key] = parseRate(draft[key]);
      }
    }
    if (draft.commission_new_end_user_bonus_rate != null) {
      const trimmed = draft.commission_new_end_user_bonus_rate.trim();
      body.commission_new_end_user_bonus_rate = trimmed === "" ? null : parseRate(trimmed);
    }
    saveMutation.mutate({ id, body });
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={pageHoverHint}>
        Commission rates
      </PageTitleWithHelp>

      {!canEdit ? (
        <p className="text-sm text-muted-foreground">
          View only — commission module administrators can edit rates and terminate reps.
        </p>
      ) : null}

      {canEdit ? (
        <Card>
          <CardHeader>
            <CardTitle>Import from Commissions.xlsx</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-wrap items-end gap-3">
            <FilePicker
              id="comm-rates-import"
              label="Comm Rate worksheet"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={importMutation.isPending}
              browseLabel={importMutation.isPending ? "Importing…" : "Choose workbook"}
              onChange={(file, event) => {
                if (file != null) {
                  importMutation.mutate(file);
                  event.target.value = "";
                }
              }}
            />
            {importMutation.isError ? (
              <p className="text-sm text-destructive">
                {importMutation.error instanceof Error ? importMutation.error.message : "Import failed."}
              </p>
            ) : null}
            {importMutation.isSuccess ? (
              <p className="text-sm text-muted-foreground">Import completed.</p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showTerminated}
              onChange={(event) => setShowTerminated(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Show terminated reps
          </label>
          {!showTerminated && terminatedHiddenCount > 0 ? (
            <p className="text-xs text-slate-500">
              Showing {visibleRows.length} of {allRows.length} rep agencies ({terminatedHiddenCount} terminated hidden).
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card className="flex min-h-0 flex-col lg:max-h-[calc(100vh-14rem)]">
        <CardHeader className="shrink-0">
          <CardTitle>Rep agencies</CardTitle>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col p-0">
          {listQuery.isPending ? <p className="px-5 py-4 text-sm text-slate-600">Loading rates…</p> : null}
          {listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-700">
              {listQuery.error instanceof Error ? listQuery.error.message : "Could not load commission rates."}
            </p>
          ) : null}
          {!listQuery.isPending && !listQuery.isError ? (
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-100">
          <table className="w-full min-w-[960px] border-collapse text-sm">
            <thead className="text-xs leading-snug text-slate-600">
              <tr>
                <th
                  rowSpan={2}
                  className="sticky left-0 top-0 z-30 min-w-[10rem] border-b border-slate-200 bg-slate-50 px-2 align-bottom font-medium text-left shadow-[1px_0_0_0_rgb(226_232_240)]"
                >
                  Rep
                </th>
                <th
                  rowSpan={2}
                  className={`${RATES_HEADER_ROW_1} align-bottom font-medium text-right whitespace-nowrap`}
                >
                  Base
                </th>
                <th
                  colSpan={3}
                  className={`${RATES_HEADER_ROW_1} border-l border-slate-200 text-right font-medium text-slate-700`}
                >
                  Tier %
                </th>
                <th
                  colSpan={2}
                  className={`${RATES_HEADER_ROW_1} border-l border-slate-200 text-right font-medium text-slate-700`}
                >
                  {previousCalendarYear}
                </th>
                <th
                  colSpan={2}
                  className={`${RATES_HEADER_ROW_1} border-l border-slate-200 text-right font-medium text-slate-700`}
                >
                  {currentCalendarYear}
                </th>
                <th
                  rowSpan={2}
                  className={`${RATES_HEADER_ROW_1} border-l border-slate-200 align-bottom font-medium text-right`}
                >
                  <span className="inline-block text-right">
                    <span className="block">New end user</span>
                    <span className="block">%</span>
                  </span>
                </th>
                <th
                  rowSpan={2}
                  className={`${RATES_HEADER_ROW_1} align-bottom font-medium text-left whitespace-nowrap`}
                >
                  Terminated
                </th>
                {canEdit ? (
                  <th rowSpan={2} className={`${RATES_HEADER_ROW_1} align-bottom text-center`} />
                ) : null}
              </tr>
              <tr>
                <th className={`${RATES_HEADER_ROW_2} border-l border-slate-200 text-right`}>0–1,000</th>
                <th className={`${RATES_HEADER_ROW_2} text-right`}>1,001–5,000</th>
                <th className={`${RATES_HEADER_ROW_2} text-right`}>5,001+</th>
                <th
                  className={`${RATES_HEADER_ROW_2} border-l border-slate-200 text-right`}
                  title={fciQuantityHint}
                >
                  FCI qty
                </th>
                <th className={`${RATES_HEADER_ROW_2} text-right`} title={bonusRateHint}>
                  <span className="inline-block text-right">
                    <span className="block">Bonus</span>
                    <span className="block">%</span>
                  </span>
                </th>
                <th
                  className={`${RATES_HEADER_ROW_2} border-l border-slate-200 text-right`}
                  title={fciQuantityHint}
                >
                  FCI qty
                </th>
                <th className={`${RATES_HEADER_ROW_2} text-right`} title={bonusRateHint}>
                  <span className="inline-block text-right">
                    <span className="block">Tentative</span>
                    <span className="block">bonus %</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const id = Number(row.id);
                const draft = drafts[id] ?? {};
                const isTerminated = isRepAgencyTerminated(row);
                return (
                  <tr key={id} className="border-b">
                    <td className={`${RATES_BODY_CELL} sticky left-0 z-[1] bg-white whitespace-nowrap`}>
                      <RepAgencyDetailLink repAgencyId={id} repAgencyName={String(row.name)} />
                    </td>
                    {(
                      [
                        ["commission_rate", row.commission_rate, ""],
                        ["commission_rate_tier_0_1000", row.commission_rate_tier_0_1000, "border-l border-slate-200"],
                        ["commission_rate_tier_1001_5000", row.commission_rate_tier_1001_5000, ""],
                        ["commission_rate_tier_5001_plus", row.commission_rate_tier_5001_plus, ""],
                      ] as const
                    ).map(([key, value, cellClass]) => (
                      <td key={key} className={`${RATES_NUMERIC_CELL} ${cellClass}`}>
                        {canEdit ? (
                          <Input
                            className={RATE_INPUT_CLASS}
                            value={draft[key] ?? formatRate(value)}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [id]: { ...current[id], [key]: event.target.value },
                              }))
                            }
                          />
                        ) : (
                          <ReadOnlyHoverLayer active hoverHint={readOnlyTitle}>
                            <span className="tabular-nums">{formatRate(value) || "—"}</span>
                          </ReadOnlyHoverLayer>
                        )}
                      </td>
                    ))}
                    <td className={`${RATES_NUMERIC_CELL} border-l border-slate-200`}>
                      <FciQuantityCell
                        repAgencyId={id}
                        calendarYear={previousCalendarYear}
                        quantity={row.prev_year_fci_quantity}
                      />
                    </td>
                    <td className={`${RATES_NUMERIC_CELL} text-slate-700`}>
                      {formatRate(row.prev_year_bonus_rate) || "—"}
                    </td>
                    <td className={`${RATES_NUMERIC_CELL} border-l border-slate-200`}>
                      <FciQuantityCell
                        repAgencyId={id}
                        calendarYear={currentCalendarYear}
                        quantity={row.current_year_fci_quantity}
                      />
                    </td>
                    <td className={`${RATES_NUMERIC_CELL} text-slate-700`}>
                      {formatRate(row.current_year_tentative_bonus_rate) || "—"}
                    </td>
                    <td className={`${RATES_NUMERIC_CELL} border-l border-slate-200`}>
                      {canEdit ? (
                        <Input
                          className={RATE_INPUT_CLASS}
                          value={
                            draft.commission_new_end_user_bonus_rate ??
                            formatRate(row.commission_new_end_user_bonus_rate)
                          }
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [id]: {
                                ...current[id],
                                commission_new_end_user_bonus_rate: event.target.value,
                              },
                            }))
                          }
                        />
                      ) : (
                        <ReadOnlyHoverLayer active hoverHint={readOnlyTitle}>
                          <span className="tabular-nums">
                            {formatRate(row.commission_new_end_user_bonus_rate) || "—"}
                          </span>
                        </ReadOnlyHoverLayer>
                      )}
                    </td>
                    <td className={`${RATES_BODY_CELL} whitespace-nowrap text-left text-slate-700`}>
                      {formatTerminatedDate(row.commission_terminated_at)}
                    </td>
                    {canEdit ? (
                      <td className={`${RATES_BODY_CELL} text-center`}>
                        <div className="flex flex-row flex-wrap items-center justify-center gap-2">
                          <Button type="button" size="sm" onClick={() => saveRow(id, draft)}>
                            Save
                          </Button>
                          <ReadOnlyHoverLayer
                            active={isTerminated}
                            hoverHint={`Already terminated on ${formatTerminatedDate(row.commission_terminated_at)}`}
                            layout="inline"
                          >
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={isTerminated || saveMutation.isPending}
                              onClick={() => setTerminateTarget({ id, name: String(row.name) })}
                            >
                              Terminate
                            </Button>
                          </ReadOnlyHoverLayer>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={canEdit ? 12 : 11}
                    className="px-5 py-4 text-sm text-slate-500"
                  >
                    {showTerminated
                      ? "No rep agencies found."
                      : "No active rep agencies. Turn on Show terminated reps to include terminated agencies."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
          ) : null}
        </CardBody>
      </Card>

      {terminateTarget != null ? (
        <TerminateRepAgencyModal
          repAgencyName={terminateTarget.name}
          initialDate={todayIsoDate()}
          onCancel={() => setTerminateTarget(null)}
          isPending={saveMutation.isPending}
          onConfirm={(terminatedAt) => {
            saveMutation.mutate({
              id: terminateTarget.id,
              body: { commission_terminated_at: terminatedAt },
            });
          }}
        />
      ) : null}
    </div>
  );
}
